import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, TrendingUp, ExternalLink, Sparkles, Star, MousePointerClick, Trophy } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { goUrl } from "@/lib/api";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Medal = ({ rank }) => {
  const styles = {
    1: { bg: "bg-[#FEF3C7]", text: "text-[#92400E]", ring: "ring-[#F59E0B]/30" },
    2: { bg: "bg-[#E2E8F0]", text: "text-[#475569]", ring: "ring-[#94A3B8]/30" },
    3: { bg: "bg-[#FED7AA]", text: "text-[#9A3412]", ring: "ring-[#F97316]/30" },
  }[rank] || { bg: "bg-[#F1F5F9]", text: "text-[#64748B]", ring: "ring-[#CBD5E1]/30" };
  return (
    <span className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs ring-1 ${styles.bg} ${styles.text} ${styles.ring}`}>
      {rank}
    </span>
  );
};

const TrendingStrip = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/trending?days=7&limit=3`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data || !data.entries || data.entries.length === 0) return null;

  return (
    <section className="bg-[#0F172A] text-white relative overflow-hidden" data-testid="trending-strip">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle at 15% 30%, #F59E0B 0%, transparent 35%), radial-gradient(circle at 85% 70%, #2563EB 0%, transparent 35%)",
        }}
        aria-hidden
      />
      <div className="hac-container relative py-12 md:py-14 max-w-6xl">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-7">
          <div>
            <div className="overline mb-2 text-[#F59E0B] inline-flex items-center gap-2">
              <Flame size={12} />
              {data.fallback
                ? (isNl ? "Best beoordeeld" : "Top-rated apps")
                : (isNl ? "Trending deze week" : "Trending this week")}
            </div>
            <h2 className="font-heading text-2xl md:text-3xl text-white leading-tight max-w-2xl">
              {data.fallback
                ? (isNl ? "De best beoordeelde paardenapps van dit moment" : "The highest-rated horse apps right now")
                : (isNl ? "Waar paardenliefhebbers deze week op klikten" : "What equestrians clicked on this week")}
            </h2>
          </div>
          <div className="text-xs text-white/60 inline-flex items-center gap-1.5 self-start mt-1">
            <TrendingUp size={12} /> {isNl ? "Activiteit deze week · reset elke maandag" : "This week's activity · resets every Monday"}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          {data.entries.map((e, i) => {
            const isHot = !data.fallback && (e.is_new || (e.pct_change !== null && e.pct_change > 0));
            const isUp = !data.fallback && e.pct_change !== null && e.pct_change > 0;
            return (
              <div
                key={e.slug}
                className="group bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 rounded-2xl p-4 transition-colors backdrop-blur-sm"
                data-testid={`trending-card-${e.slug}`}
              >
                <div className="flex items-start gap-3">
                  <Medal rank={i + 1} />
                  <span
                    className="w-10 h-10 flex items-center justify-center text-white font-bold rounded-lg text-base shrink-0"
                    style={{ backgroundColor: e.color || "#2563EB" }}
                  >
                    {e.name?.charAt(0)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/apps/${e.slug}`} className="font-bold text-white hover:text-[#F59E0B] transition-colors truncate">
                        {e.name}
                      </Link>
                      {isHot && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-[#F59E0B] bg-[#F59E0B]/15 px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`trending-badge-${e.slug}`}>
                          <Flame size={10} />
                          {e.is_new
                            ? (isNl ? "Nieuw" : "New")
                            : isUp
                              ? `+${e.pct_change}%`
                              : `${e.pct_change}%`}
                        </span>
                      )}
                    </div>
                    {e.slug === "equisight" && (
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#F59E0B] mt-1 inline-flex items-center gap-1" data-testid="trending-equisight-tagline">
                        <Sparkles size={10} />
                        {isNl ? "Meest belovende app voor 2026" : "Most promising app for 2026"}
                      </div>
                    )}
                    <p className="text-xs text-white/60 mt-0.5 truncate">{e.tagline}</p>
                    <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                      <span className="text-[11px] text-white/50 inline-flex items-center gap-1">
                        <Star size={10} className="fill-[#F59E0B] text-[#F59E0B]" /> {e.rating?.toFixed(1)}
                      </span>
                      {e.clicks > 0 && (
                        <span className="text-[11px] text-white/60 inline-flex items-center gap-1" data-testid={`trending-clicks-${e.slug}`}>
                          <MousePointerClick size={10} /> {e.clicks} {isNl ? (e.clicks === 1 ? "klik" : "klikken") : (e.clicks === 1 ? "click" : "clicks")}
                        </span>
                      )}
                      {(e.real_clicks ?? 0) > 0 && (
                        <span className="text-[10px] text-emerald-300/90 bg-emerald-500/15 border border-emerald-400/20 px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`trending-reason-${e.slug}`} title={isNl ? "Bevat live klikken van echte gebruikers deze week" : "Includes live clicks from real users this week"}>
                          <Flame size={9} /> {isNl ? "Live" : "Live"}
                        </span>
                      )}
                      {e.reason === "highest_rated" && (e.real_clicks ?? 0) === 0 && (
                        <span className="text-[10px] text-sky-300/90 bg-sky-500/15 border border-sky-400/20 px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`trending-reason-${e.slug}`} title={isNl ? "Geselecteerd op basis van hoogste rating en score" : "Selected based on highest rating and score"}>
                          <Trophy size={9} /> {isNl ? "Best beoordeeld" : "Highest rated"}
                        </span>
                      )}
                      <span className="text-[11px] text-white/40 ml-auto">{e.country}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-2">
                  <Link
                    to={`/apps/${e.slug}`}
                    className="text-xs text-white/70 hover:text-white px-2 py-1"
                    data-testid={`trending-review-${e.slug}`}
                  >
                    {isNl ? "Review" : "Review"}
                  </Link>
                  <a
                    href={goUrl(e.slug, "trending", { campaign: "trending-week" })}
                    target="_blank"
                    rel="noopener sponsored nofollow noreferrer"
                    className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 bg-[#F59E0B] text-[#0F172A] text-xs font-bold rounded-full hover:bg-[#FBBF24]"
                    data-testid={`trending-cta-${e.slug}`}
                  >
                    {isNl ? "Probeer" : "Try it"} <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-end">
          <Link to="/rankings" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1">
            <Sparkles size={12} />
            {isNl ? "Bekijk de volledige Top 100" : "View the full Top 100"} →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TrendingStrip;
